import React, { useEffect, useState } from 'react';
import { useLang } from '../../contexts/LangContext';

export type FeeNotificationActionMode = 'MONTHLY_FEE' | 'DEBT_REMINDER';

interface FeeNotificationDeliveryModalProps {
  isOpen: boolean;
  mode: FeeNotificationActionMode | null;
  onClose: () => void;
  onConfirm: (deliveryMethods: string[]) => void;
  sending?: boolean;
}

const FeeNotificationDeliveryModal: React.FC<FeeNotificationDeliveryModalProps> = ({
  isOpen,
  mode,
  onClose,
  onConfirm,
  sending = false,
}) => {
  const { t } = useLang();
  const [deliveryMethods, setDeliveryMethods] = useState<string[]>(['EMAIL']);

  useEffect(() => {
    if (isOpen) {
      setDeliveryMethods(['EMAIL']);
    }
  }, [isOpen, mode]);

  if (!isOpen || !mode) {
    return null;
  }

  const toggleMethod = (method: string) => {
    setDeliveryMethods((prev) => (
      prev.includes(method)
        ? prev.filter((item) => item !== method)
        : [...prev, method]
    ));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (deliveryMethods.length === 0) {
      return;
    }
    onConfirm(deliveryMethods);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {mode === 'DEBT_REMINDER' ? t('sendDebtReminderNotification') : t('sendFeeNotification')}
          </h2>
          <p className="text-sm text-gray-600 mt-1">{t('selectFeeNotificationDeliveryMethods')}</p>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={deliveryMethods.includes('EMAIL')}
              onChange={() => toggleMethod('EMAIL')}
              className="mt-1 rounded border-gray-300 text-blue-600"
            />
            <span>
              <span className="block text-sm font-medium text-gray-900">{t('deliveryMethodEmail')}</span>
              <span className="block text-xs text-gray-500">{t('deliveryMethodEmailFeeHint')}</span>
            </span>
          </label>

          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={deliveryMethods.includes('APP')}
              onChange={() => toggleMethod('APP')}
              className="mt-1 rounded border-gray-300 text-blue-600"
            />
            <span>
              <span className="block text-sm font-medium text-gray-900">{t('deliveryMethodAppPush')}</span>
              <span className="block text-xs text-gray-500">{t('deliveryMethodAppFeeHint')}</span>
            </span>
          </label>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={sending}
              className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {t('cancel')}
            </button>
            <button
              type="submit"
              disabled={sending || deliveryMethods.length === 0}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
            >
              {sending ? t('saving') : t('confirmSendNotification')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FeeNotificationDeliveryModal;
